/**
 * Договори ЦПУ — список (канон Норми #29, 2026-08-23): InfiniteDocGrid,
 * серверні фільтри орг/чинність/ПІБ + сортування, колонки «Організація»,
 * «Підпис» (sign-control).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { civilLawContracts, type CivilLawContract } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Чернетка', POSTED: '✓ Проведено', CANCELLED: '✗ Скасовано',
};
type ActiveFilter = '' | 'active' | 'inactive';
const dateOnly = (p: { value?: string | null }) =>
  p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '';

export default function CivilLawContractsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const [q, setQ] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<CivilLawContract>(
    (limit, offset, sort) => civilLawContracts.list({
      organization_id: orgId || undefined,
      q: q.trim() || undefined,
      active: activeFilter || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, q, activeFilter],
    { attachmentsType: 'doc.civil_law_contracts' },
  );
  const sc = useSignControlInfinite<CivilLawContract>('doc.civil_law_contracts', orgId);

  const cols = useMemo<ColDef<CivilLawContract>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: CivilLawContract }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 130 },
    {
      field: 'organization_id', headerName: 'Організація', width: 190,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    { field: 'document_date', headerName: 'Дата', width: 120, valueFormatter: dateOnly },
    { field: 'employee_name', headerName: 'Працівник', flex: 1, minWidth: 200 },
    { field: 'date_from', headerName: 'З', width: 110, valueFormatter: dateOnly },
    { field: 'date_to', headerName: 'По', width: 110, valueFormatter: dateOnly },
    {
      field: 'total_amount', headerName: 'Сума', width: 130,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'state', headerName: 'Стан', width: 130,
      valueFormatter: (p) => p.data ? (STATE_LABEL[p.value] ?? p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], [orgName]);

  const selStyle = { width: 150, flex: '0 0 auto' } as const;

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Договори ЦПУ<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={() => navigate('/employees/new-unified')}>
          + Новий
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <select value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
                style={selStyle}>
          <option value="">Усі</option>
          <option value="active">Лише чинні</option>
          <option value="inactive">Лише не чинні</option>
        </select>
        <input placeholder="ПІБ працівника…" value={q}
               onChange={(e) => setQ(e.target.value)}
               style={{ width: 180, flex: '0 0 auto' }} />
      </div>
      <InfiniteDocGrid<CivilLawContract>
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="civil-law-contracts"
        onRowDoubleClicked={(r) => navigate(`/civil-law-contracts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
